import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
} from 'react-native';
import {
  responsiveWidth,
  responsiveHeight,
} from 'react-native-responsive-dimensions';

import {text} from '../../text';
import {svg} from '../../assets/svg';
import {theme} from '../../constants';
import {components} from '../../components';
import {setScreen} from '../../store/slices/tabSlice';
import BottomTabBar from '../../navigation/BottomTabBar';
import {
  useAppSelector,
  useAppNavigation,
  useAppDispatch,
} from '../../hooks';
import {useValidatePromocodeMutation} from '../../store/slices/apiSlice';
import {formatCurrency} from '../../utils/currency';

const calculateDiscountAmount = (
  discountValue: number,
  totalValue: number,
): number => {
  if (!Number.isFinite(discountValue) || discountValue <= 0) {
    return 0;
  }

  const normalizedTotal = Number.isFinite(totalValue) ? totalValue : 0;

  if (discountValue > 0 && discountValue <= 1) {
    return normalizedTotal * discountValue;
  }

  if (discountValue > 100) {
    return Math.min(discountValue, normalizedTotal);
  }

  return (normalizedTotal * discountValue) / 100;
};

const Order: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigation = useAppNavigation();
  const cart = useAppSelector((state) => state.cartSlice.list);
  const subtotal = useAppSelector((state) => state.cartSlice.subtotal);
  const delivery = useAppSelector((state) => state.cartSlice.delivery);
  const totalFromCart = useAppSelector((state) =>
    Number(state.cartSlice.total),
  );
  const deliveryFromCart = useAppSelector((state) =>
    Number(state.cartSlice.delivery),
  );

  const [promocode, setPromocode] = useState('');
  const [total, setTotal] = useState(
    (totalFromCart + deliveryFromCart).toFixed(2),
  );
  const [discount, setDiscount] = useState(0);
  const [validatePromocode, {isLoading: isValidating}] =
    useValidatePromocodeMutation();

  useEffect(() => {
    setTotal((totalFromCart + deliveryFromCart - discount).toFixed(2));
  }, [totalFromCart, deliveryFromCart, discount]);

  const applyPromoCode = async () => {
    const trimmedCode = promocode.trim();

    if (!trimmedCode) {
      Alert.alert('Promocode', 'Please enter a valid promocode to apply.');
      return;
    }

    try {
      const response = await validatePromocode({promocode: trimmedCode}).unwrap();
      const promo = response?.promocode;

      if (promo?.discount) {
        const grossTotal = totalFromCart + deliveryFromCart;
        const discountAmount = calculateDiscountAmount(
          promo.discount,
          grossTotal,
        );
        const nextTotal = Math.max(grossTotal - discountAmount, 0);

        setDiscount(discountAmount);
        setTotal(nextTotal.toFixed(2));
        Alert.alert('Promocode applied', 'Your promocode has been applied.');
      } else {
        Alert.alert('Promocode', 'Promo code unavailable.');
      }
    } catch (error: any) {
      const message =
        error?.data?.message ??
        error?.error ??
        'Promo code unavailable.';
      Alert.alert('Promocode', message);
    }
  };

  const renderStatusBar = () => {
    return <components.StatusBar />;
  };

  const renderHeader = () => {
    return <components.Header basket={true} userImage={true} />;
  };

  const renderDishes = () => {
    return (
      <View>
        {cart.map((item, index, array) => {
          const last = index === array.length - 1;
          return (
            <components.OrderItem
              item={item}
              key={item.cartItemId ?? item.id}
              containerStyle={{
                marginBottom: last ? 20 : 14,
              }}
            />
          );
        })}
      </View>
    );
  };

  const renderPromoCodeApplied = () => {
    if (discount > 0) {
      return (
        <View style={{marginBottom: responsiveHeight(7)}}>
          <svg.CodeAppliedSvg />
        </View>
      );
    }

    return null;
  };

  const renderPromoCodeInput = () => {
    if (discount === 0) {
      return (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            height: 50,
            borderWidth: 1,
            borderRadius: 10,
            borderColor: theme.colors.accentPrimary,
            marginBottom: 30,
          }}
        >
          <View style={{flex: 1, paddingLeft: 14}}>
            <TextInput
              placeholder='Enter your promocode'
              value={promocode}
              onChangeText={(text) => setPromocode(text)}
              style={{
                ...theme.fonts.DMSans_400Regular,
                fontSize: 14,
                color: theme.colors.textPrimary,
              }}
              editable={!isValidating}
              autoCapitalize='characters'
            />
          </View>
          <TouchableOpacity
            style={{
              width: '30%',
              height: '100%',
              backgroundColor: theme.colors.accentPrimary,
              borderRadius: 10,
              justifyContent: 'center',
              alignItems: 'center',
              opacity: isValidating ? 0.7 : 1,
            }}
            onPress={applyPromoCode}
            disabled={isValidating}
          >
            {isValidating && (
              <ActivityIndicator size='small' color={theme.colors.backgroundPrimary} />
            )}
            {!isValidating && (
              <Text
                style={{
                  textTransform: 'uppercase',
                  ...theme.fonts.DMSans_500Medium,
                  fontSize: 14,
                  lineHeight: 14 * 1.2,
                  color: theme.colors.backgroundPrimary,
                }}
              >
                apply
              </Text>
            )}
          </TouchableOpacity>
        </View>
      );
    }

    return null;
  };

  const renderOrderSummary = () => {
    return (
      <View
        style={{
          padding: 20,
          borderWidth: 1,
          borderRadius: 10,
          borderColor: theme.colors.accentPrimary,
          marginBottom: 30,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottomWidth: 1,
            borderBottomColor: '#DBE9F5',
            paddingBottom: 10,
            marginBottom: 20,
          }}
        >
          <Text
            style={{
              ...theme.fonts.DMSans_500Medium,
              fontSize: 14,
              lineHeight: 14 * 1.2,
              color: theme.colors.textPrimary,
            }}
          >
            Subtotal
          </Text>
          <text.T14 style={{color: theme.colors.textPrimary}}>
            {formatCurrency(subtotal)}
          </text.T14>
        </View>
        {discount > 0 && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 10,
            }}
          >
            <text.T14>Discount</text.T14>
            <text.T14>- {formatCurrency(discount)}</text.T14>
          </View>
        )}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 10,
          }}
        >
          <text.T14>Delivery</text.T14>
          <text.T14>{formatCurrency(delivery)}</text.T14>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <text.H4>Total</text.H4>
          <text.H4>{formatCurrency(total)}</text.H4>
        </View>
      </View>
    );
  };

  const renderOrder = () => {
    return (
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 20,
          paddingTop: 10,
        }}
      >
        {renderDishes()}
        {renderPromoCodeApplied()}
        {renderPromoCodeInput()}
        {renderOrderSummary()}
      </ScrollView>
    );
  };

  const renderEmptyCart = () => {
    const imageSize = Math.min(300, responsiveWidth(70));

    return (
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          backgroundColor: theme.colors.backgroundPrimary,
          paddingHorizontal: 20,
          marginHorizontal: 20,
          justifyContent: 'center',
          alignItems: 'center',
          borderRadius: 10,
          marginTop: 5,
          paddingVertical: 20,
        }}
      >
        <components.Image
          source={{uri: 'https://george-fx.github.io/dine-hub/14.jpg'}}
          resizeMode='contain'
          style={{
            width: imageSize,
            height: imageSize,
            alignSelf: 'center',
          }}
        />
        <text.H2 style={{marginTop: 30, marginBottom: 14}}>
          Your cart is empty!
        </text.H2>
        <text.T16 style={{textAlign: 'center'}}>
          Looks like you haven't made{'\n'}your order yet.
        </text.T16>
      </ScrollView>
    );
  };

  const renderContent = () => {
    return (
      <React.Fragment>
        {cart.length === 0 ? renderEmptyCart() : renderOrder()}
      </React.Fragment>
    );
  };

  const renderButton = () => {
    return (
      <components.Button
        title={cart.length === 0 ? 'Shop now' : 'Checkout'}
        containerStyle={{
          paddingHorizontal: 20,
          paddingTop: 20,
        }}
        loading={false}
        onPress={() => {
          if (cart.length === 0) {
            dispatch(setScreen('Menu'));
            return;
          }

          if (cart.length > 0) {
            navigation.navigate('Checkout', {
              total,
              subtotal,
              discount,
              delivery,
            });
            return;
          }
        }}
      />
    );
  };

  const renderBottomTabBar = () => {
    return <BottomTabBar />;
  };

  const renderHomeIndicator = () => {
    return <components.HomeIndicator />;
  };

  return (
    <components.SmartView>
      {renderStatusBar()}
      {renderHeader()}
      {renderContent()}
      {renderButton()}
      {renderBottomTabBar()}
      {renderHomeIndicator()}
    </components.SmartView>
  );
};

export default Order;
